// The client side of /api/mail (BUILD-BRIEF-R2 §7) — owned by the `mail` agent.
//
// One rule shows through every signature here: there is no `send(to, subject, body)`. The only
// way a message leaves is `sendOutbox(id)` — an id the operator can see, on a row an operator
// already held. Composing and sending are two clicks apart, on purpose (§0).
import { qs, request } from './client.js';
import type {
  ListResponse, MailMessage, MailSummary, MailThread, MailTriage, OutboxItem, WritingProfile,
} from '../../shared/types.js';

/** The rail's five counts plus the seven-day split its detail lines print. */
export interface MailSummaryFull extends MailSummary {
  messages_in: number;
  messages_out: number;
  median_reply_hours: number | null;
}

/** Also the source of the sidebar's `inbox.needs_me` count — AppShell polls it and treats any
 *  failure as silence, so this route being absent leaves the nav quiet, never zeroed. */
export function mailSummary(): Promise<MailSummaryFull> {
  return request('/mail/summary');
}

export function listThreads(params: { filter?: string; limit?: number } = {}): Promise<ListResponse<MailThread>> {
  return request(`/mail/threads${qs(params)}`);
}

/** A thread at full dress: every message in order, and the drafts waiting to answer it. */
export interface ThreadDetail {
  thread: MailThread;
  messages: MailMessage[];
  drafts: OutboxItem[];
}

export function getThread(key: string): Promise<ThreadDetail> {
  return request(`/mail/threads/${encodeURIComponent(key)}`);
}

export function listTriage(params: { status?: string } = {}): Promise<ListResponse<MailTriage>> {
  return request(`/mail/triage${qs(params)}`);
}

export function triageCreatePerson(id: number): Promise<MailTriage> {
  return request(`/mail/triage/${id}/create-person`, { method: 'POST' });
}

export function triageAttach(id: number, personId: number): Promise<MailTriage> {
  return request(`/mail/triage/${id}/attach`, { method: 'POST', body: JSON.stringify({ person_id: personId }) });
}

export function triageIgnore(id: number, scope: 'address' | 'domain' = 'address'): Promise<MailTriage> {
  return request(`/mail/triage/${id}/ignore`, { method: 'POST', body: JSON.stringify({ scope }) });
}

export function listOutbox(params: { status?: string } = {}): Promise<ListResponse<OutboxItem>> {
  return request(`/mail/outbox${qs(params)}`);
}

/** The ONLY send path in the app, and it is an operator click. No capability reaches it. */
export function sendOutbox(id: number): Promise<OutboxItem> {
  return request(`/mail/outbox/${id}/send`, { method: 'POST' });
}

export function writingProfile(personId: number): Promise<WritingProfile> {
  return request(`/mail/profile/${personId}`);
}

/** Which sender is configured, whether inbound is live, and what is waiting in the drop folder. */
export interface MailHealthResponse {
  mode: 'stub' | 'smtp';
  ok?: boolean;
  error?: string | null;
  inbound?: 'imap' | 'drop' | 'off' | null;
  last_sync_at?: string | null;
  drop_dir?: string | null;
  imap?: { configured: boolean; connected: boolean; host: string | null; folders: string[]; last_sync: string | null };
  pending?: number;
  hourly_cap?: { cap: number; used: number; remaining: number } | null;
}

export function mailHealth(): Promise<MailHealthResponse> {
  return request('/mail/health');
}
